import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaDownload, FaMapMarkerAlt, FaSpinner } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { clientPods } from '../api';

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const STATUS_META = {
  pending: { label: 'Awaiting approval', color: '#b45309', bg: '#fffbeb' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4' },
  received: { label: 'Received', color: '#16a34a', bg: '#f0fdf4' },
  rejected: { label: 'Query raised', color: '#dc2626', bg: '#fef2f2' }
};

function ClientPods({ onLogout }) {
  const navigate = useNavigate();
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [pods, setPods] = useState([]);
  const [counts, setCounts] = useState({ total: 0, awaiting_approval: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await clientPods.list();
        if (cancelled) return;
        if (res && res.success) {
          setPods(res.pods || []);
          setCounts(res.counts || { total: (res.pods || []).length, awaiting_approval: 0, approved: 0 });
        } else {
          setError((res && res.error) || 'Could not load your proofs of delivery.');
        }
      } catch (err) {
        console.error('PODs error:', err);
        if (!cancelled) setError('Could not load your proofs of delivery. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <ClientLayout activeTab="pods" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>📝 Proof of Delivery</h1>
          <p className="text-sm mt-1" style={{ color: '#4A5568' }}>
            Signed delivery notes for every load we have completed for you.
          </p>
        </div>
        <button
          onClick={() => navigate('/client-jobs')}
          className="px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50"
          style={{ borderColor: '#E8ECEF', color: '#0A1929' }}
        >
          View my jobs
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border p-4 mb-6 text-sm" style={{ borderColor: '#fecaca', backgroundColor: '#fef2f2', color: '#b91c1c' }}>{error}</div>
      ) : null}

      {!loading && counts.total > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'PODs on file', value: counts.total, color: '#0A1929' },
            { label: 'Awaiting approval', value: counts.awaiting_approval || 0, color: '#b45309' },
            { label: 'Approved', value: counts.approved || 0, color: '#16a34a' }
          ].map((c) => (
            <div key={c.label} className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#94A3B8' }}>{c.label}</p>
              <p className="text-2xl font-bold" style={{ color: c.color }}>{c.value}</p>
            </div>
          ))}
        </div>
      )}


      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-3xl mx-auto mb-4" style={{ color: '#00A86B' }} />
            <p className="text-gray-500">Loading your PODs...</p>
          </div>
        </div>
      ) : pods.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: '#E8ECEF' }}>
          <FaCheckCircle className="text-4xl mx-auto mb-4" style={{ color: '#CBD5E1' }} />
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#0A1929' }}>No proofs of delivery yet</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            As soon as a load is delivered, the signed POD is uploaded by our team or the carrier — it will then appear here for you to download.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {pods.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.pending;
            return (
              <div key={p.id} className="bg-white rounded-xl border p-5" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="font-semibold" style={{ color: '#0A1929' }}>
                      {p.pod_number || p.load_number || p.job_number || 'POD'}
                    </p>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>
                      {[p.job_number, p.leg_label, p.load_number].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap" style={{ color: meta.color, backgroundColor: meta.bg }}>
                    {meta.label}
                  </span>
                </div>

                <p className="text-sm flex items-start gap-2 mb-3" style={{ color: '#4A5568' }}>
                  <FaMapMarkerAlt size={12} className="mt-1 flex-shrink-0" style={{ color: '#00A86B' }} />
                  <span>{p.route || 'Route not recorded'}</span>
                </p>

                <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5" style={{ color: '#94A3B8' }}>Delivered</p>
                    <p style={{ color: '#0A1929' }}>{fmtDateTime(p.delivered_at)}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5" style={{ color: '#94A3B8' }}>Received by</p>
                    <p style={{ color: '#0A1929' }}>{p.receiver || '—'}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5" style={{ color: '#94A3B8' }}>Weight</p>
                    <p style={{ color: '#0A1929' }}>{p.weight_mt != null ? `${p.weight_mt} MT` : '—'}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5" style={{ color: '#94A3B8' }}>Commodity</p>
                    <p style={{ color: '#0A1929' }}>{p.commodity || '—'}</p>
                  </div>
                </div>

                {p.image_url ? (
                  <a
                    href={p.image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold"
                    style={{ backgroundColor: '#00A86B' }}
                  >
                    <FaDownload size={12} /> View / download the POD
                  </a>
                ) : (
                  <p className="text-xs" style={{ color: '#94A3B8' }}>The POD document has not been uploaded yet.</p>
                )}
              </div>
            );
          })}
        </div>
      )}

    </ClientLayout>
  );
}

export default ClientPods;
